import type { ClientHealth, ClientStatusDto } from '@citilight-monitor/contracts';
import { cn } from '../../lib/cn';
import { HEALTH_CHIP, HEALTH_LABELS, isServer } from '../../lib/metrics';

/** Problems first, healthy last: the order the bar and the numbers read in. */
const ORDER: ClientHealth[] = ['firing', 'unreachable', 'stale', 'no_data', 'healthy'];

interface FleetHealthStatsProps {
  clients: ClientStatusDto[];
  filter: ClientHealth | null;
  onFilter: (health: ClientHealth | null) => void;
}

/**
 * Headline numbers on the band: clients per state and one bar of their shares.
 * Each number is also the filter for the client list below.
 */
export function FleetHealthStats({ clients, filter, onFilter }: FleetHealthStatsProps) {
  const counts = new Map<ClientHealth, number>();
  const serverCounts = new Map<ClientHealth, number>();
  for (const c of clients) {
    counts.set(c.health, (counts.get(c.health) ?? 0) + 1);
    if (isServer(c.client.metrics)) serverCounts.set(c.health, (serverCounts.get(c.health) ?? 0) + 1);
  }
  // Always show the four main states so the row keeps its shape; "No data yet" only when present.
  const shown = ORDER.filter((h) => h !== 'no_data' || counts.get(h));

  return (
    <div className="flex flex-col gap-4">
      <div role="group" aria-label="Filter clients by status" className="grid grid-cols-2 gap-y-4 sm:flex sm:flex-wrap">
        {shown.map((health) => {
          const count = counts.get(health) ?? 0;
          const serverCount = serverCounts.get(health) ?? 0;
          const appCount = count - serverCount;
          const selected = filter === health;
          return (
            <button
              key={health}
              type="button"
              aria-pressed={selected}
              disabled={count === 0}
              onClick={() => onFilter(selected ? null : health)}
              className={cn(
                'group flex cursor-pointer flex-col items-start gap-1 rounded-xl px-4 py-2 text-left transition-colors sm:min-w-[170px] sm:border-r sm:border-band-line sm:first:pl-0 sm:[&:last-child]:border-r-0',
                'disabled:cursor-default',
                selected ? 'bg-band-raised sm:first:pl-4' : count > 0 && 'hover:bg-band-raised/60',
              )}
            >
              <span className="flex items-center gap-2 text-sm text-band-fg-secondary">
                <span className={cn('size-2.5 rounded-[3px]', HEALTH_CHIP[health].swatch, count === 0 && 'opacity-40')} aria-hidden />
                {HEALTH_LABELS[health].label}
              </span>
              <span className={cn('font-display text-5xl font-bold leading-none', count === 0 ? 'text-band-fg-muted' : 'text-band-fg')}>{count}</span>
              {serverCount > 0 && appCount > 0 && (
                <span className="text-xs text-band-fg-muted">
                  {serverCount} server{serverCount !== 1 ? 's' : ''} · {appCount} app{appCount !== 1 ? 's' : ''}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {clients.length > 0 && (
        <div className="flex h-2.5 gap-[3px]" aria-hidden>
          {shown
            .filter((h) => counts.get(h))
            .map((health) => (
              <div
                key={health}
                className={cn('rounded-[3px] transition-opacity', HEALTH_CHIP[health].swatch, filter && filter !== health && 'opacity-30')}
                style={{ flexGrow: counts.get(health) }}
              />
            ))}
        </div>
      )}
    </div>
  );
}
